/*
  # Pressable Component:
  A reusable and accessible pressable component that wraps React Native's `Pressable`
  with consistent styling patterns and enhanced accessibility support.

  ## Features
  - Wraps React Native's `Pressable` with MADS styling conventions
  - Supports all standard Pressable props
  - Uses `sx` prop for styling consistency with other MADS components
  - Provides a foundation for future React Strict DOM migration

  ## Usage

  ```tsx
  import { Pressable } from '@indeed/mads';

  <Pressable
    onPress={() => console.log('Pressed!')}
    sx={{ padding: 16 }}
    accessibilityLabel="Tap me"
  >
    <Text>Press me</Text>
  </Pressable>
  ```
 */

import React from 'react';
import {
    Pressable as RNPressable,
    type PressableProps as RNPressableProps,
    type StyleProp,
    type ViewStyle,
} from 'react-native';

export type PressableStyle = ViewStyle | ((state: { pressed: boolean }) => StyleProp<ViewStyle>);

export interface PressableProps extends Omit<RNPressableProps, 'style'> {
    /**
     * Style prop using MADS convention. Can be a ViewStyle object or a function
     * that receives press state and returns a ViewStyle.
     */
    readonly sx?: PressableStyle | StyleProp<ViewStyle>;

    /**
     * Children to render inside the pressable
     */
    readonly children?: React.ReactNode | ((state: { pressed: boolean }) => React.ReactNode);

    /**
     * Ref to the underlying Pressable component
     */
    readonly ref?: React.Ref<React.ComponentRef<typeof RNPressable>>;
}

/**
 * Pressable component that wraps React Native's Pressable with MADS styling conventions.
 * Use this component for custom pressable areas that don't fit the Button component's use case.
 */
export function Pressable({ sx, children, ref, ...props }: PressableProps): React.JSX.Element {
    return (
        <RNPressable ref={ref} style={sx} {...props}>
            {children}
        </RNPressable>
    );
}
